// TokenAvatar — canonical avatar (Paper K3T-0 ring + K3O-0 badge). Reused by
// the token page, discover rows, search, etc.
//   Ring (K3T-0): rounded-square, 2px. Bonding (launchpad pre-migration) →
//   green --buy-delta track @30% + progress tracing the bonding %. Dex /
//   migrated → full --warn ring. (OW5-0 spec.)
//   Badge (K3O-0): 16px round, --bg-chip ground; border = source brand colour
//   (sourceColor). Only STON.fi shows a logo inside — every other source is the
//   coloured ring alone.
//   Art slot: square corners filled black (--canvas), not a gradient.

import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import {
  TOKEN_IMAGE_LOAD_TIMEOUT_MS,
  TOKEN_IMAGE_RETRY_DELAY_MS,
  canonicalizeMirroredTokenAssetURL,
  tokenImageStatusCache,
} from '../common/tokenImage'
import styles from './TokenAvatar.module.css'
import {
  GOLD_STOPS,
  SourceLogo,
  goldRing,
  sourceKey,
  sourceRing,
  sourceStops,
} from './icons/SourceLogo'

// Rounded-square ring (corner radius 9 — v8 «Радиус: аватар −», 2px inset so
// the stroke sits fully inside). Drawn as a <rect pathLength=100> —
// geometrically identical to the old hand-built path, but the corner radius is
// a presentation attribute, so a theme scope can retune it via CSS `rx`.
// 52 → the canonical Discover / token-page ring; 74 → the larger Pulse ring.
const RING_RADIUS = 9
// v8: тонкое кольцо, трек заметнее грунта (ось «Кольца: трек ярче»)
const RING_WIDTH = 1.2
const TRACK_OPACITY = 0.55

// The rect path starts at (x+rx, y) — top-left after the corner — while the
// bonding arc must start at TOP-CENTER (the old hand-built path did). Shift
// the dash pattern forward by the start→top-center distance, normalised to
// pathLength=100. (If a theme scope overrides rx via CSS, the start drifts by
// ~1 unit at ±2px — negligible; at rx→max the rect start IS top-center.)
function dashStartOffset(size: number): number {
  const inner = size - 4
  const perimeter = 4 * inner - 8 * RING_RADIUS + 2 * Math.PI * RING_RADIUS
  return (100 * (inner / 2 - RING_RADIUS)) / perimeter
}

// Dash-пропсы прогресса: частичное кольцо стартует с топ-центра; полное
// (dex/мигрировано) рисуется сплошным — дэш со сдвигом оставлял бы разрыв.
function progressDashProps(pct: number, size: number) {
  if (pct >= 100) return {}
  return { strokeDasharray: `${pct} 100`, strokeDashoffset: -dashStartOffset(size) }
}

function showAvatarFallback(atomicImage: boolean, imageUrl: string | null | undefined): boolean {
  return !atomicImage || imageUrl == null
}

interface Props {
  imageUrl?: string
  /** Source token for the initial fallback. */
  label: string
  /** DEX of the pool (stonfi_v2 / dedust_v2 …). */
  dex?: string
  /** Launchpad the token graduated from, if any. */
  launchpadSource?: string | null
  /** Bonding completion 0–100 for launchpad-bonding tokens; null/absent →
      a dex/migrated token gets the full --warn ring. */
  bondingPct?: number | null
  /** Avatar edge length in px (ring box). Default 52 (Discover / token page);
      Pulse cards pass 74. Art, badge and ring all scale from this. */
  size?: number
  /** Round source-badge diameter. Default 20 (Discover); Pulse passes 18. */
  badgeSize?: number
  /** Кольцо. Default true — десктоп не меняется. false: арт на весь бокс. */
  ring?: boolean
  /** Бейдж источника. Default true. false — холдинги без площадки. */
  badge?: boolean
  /** Приглушить обводку бейджа (мобильные ленты). */
  badgeDim?: boolean
  /** Art-slot background when there is no image (any CSS value). Default: the
      flat black --canvas slot; Pulse passes the --avatar-fallback gradient. */
  artBackground?: string
  /** Art-slot corner radius in px. Default: the DS --radius-avatar (11). Activity
      rows pass a tighter radius (9) to match the artboard. */
  artRadius?: number
  /** Browser loading policy. Default eager: mirrored logos are ≤160px
      thumbnails (~3–6KB), so every list paints all of them at once —
      lazy-on-scroll reads as missing logos (LOGO-THUMBNAILS). */
  imageLoading?: 'eager' | 'lazy'
  imageFetchPriority?: 'high' | 'low' | 'auto'
  /** Keep identity-sensitive surfaces on a neutral skeleton until the mirrored
      image has loaded; never flash the label initial in its place. */
  atomicImage?: boolean
}

/** The source badge's box and its mark, in whole pixels, for a nominal
 *  `badgeSize`. The badge is drawn at 0.85 of its nominal size (the smaller
 *  badge of the v8 avatar). A CSS `scale(0.85)` used to do that and left it on
 *  fractional pixels — 18 became 15.3 with a 1.275px rim and a 9.35px mark —
 *  so it blurred and its mark sat a fraction off its middle (operator
 *  2026-10-11). Here the box is that size rounded, the rim is 1px, and the
 *  mark is the nearest size to the old one that leaves the same whole number
 *  of pixels on each side of it. */
export function badgeGeometry(badgeSize: number): { box: number; mark: number } {
  const box = Math.round(badgeSize * 0.85)
  const inside = box - 2
  const wanted = badgeSize * 0.6 * 0.85
  // the size nearest to the old mark with the inside's parity
  const mark = inside - 2 * Math.round((inside - wanted) / 2)
  return { box, mark }
}

export function TokenAvatar({
  imageUrl,
  label,
  dex,
  launchpadSource,
  bondingPct,
  size = 52,
  badgeSize = 20,
  ring = true,
  badge = true,
  badgeDim = false,
  artBackground,
  artRadius,
  imageLoading = 'eager',
  imageFetchPriority = 'auto',
  atomicImage = false,
}: Props) {
  const source = launchpadSource || dex
  // launchpad_source is set ONLY post-migration (API contract), so it decides
  // migration: a graduated token gets the gold ring even if a stale
  // bonding_progress_pct leaks through — keeping this aligned with TokenBar's
  // B.Curve (= 100% once migrated). Still-bonding (no launchpad_source) keeps
  // its launchpad colour and traces its real %.
  const migrated = !!launchpadSource
  const knownPct =
    typeof bondingPct === 'number' && Number.isFinite(bondingPct)
      ? Math.max(0, Math.min(100, bondingPct))
      : null
  const stillBondingLaunchpad = !migrated && isLaunchpadSource(source)
  // Ring fill = launch completeness: launchpad bonding -> real %, unknown -> 0.
  const pct = stillBondingLaunchpad ? (knownPct ?? 0) : 100
  const stops = migrated ? GOLD_STOPS : sourceStops(source)
  const ringBg = migrated ? goldRing() : sourceRing(source)
  const ringId = `ring-${migrated ? 'gold' : sourceKey(source)}`
  const initial = (label || '?').trim().charAt(0).toUpperCase()
  const canonicalImageUrl = canonicalizeMirroredTokenAssetURL(imageUrl)
  const showFallback = showAvatarFallback(atomicImage, canonicalImageUrl)
  const badgeBox = badgeGeometry(badgeSize)
  return (
    <div className={styles.avatar} style={{ width: size, height: size }}>
      <div
        className={styles.art}
        style={{
          width: ring ? size - 8 : size,
          height: ring ? size - 8 : size,
          ...(ring ? null : { top: 0, left: 0 }),
          ...(artBackground ? { background: artBackground } : null),
          ...(artRadius != null ? { borderRadius: artRadius } : null),
        }}
      >
        {showFallback && (
          <span className={styles.fallback} data-token-avatar-fallback="true" aria-hidden="true">
            {initial}
          </span>
        )}
        {canonicalImageUrl && (
          <TokenAvatarImage
            key={canonicalImageUrl}
            src={canonicalImageUrl}
            alt={label}
            loading={imageLoading}
            fetchPriority={imageFetchPriority}
            atomic={atomicImage}
          />
        )}
      </div>
      {ring ? (
        <svg
          className={styles.ring}
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id={ringId}
              x1="0"
              y1="0"
              x2={size}
              y2={size}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={stops[0]} />
              <stop offset="55%" stopColor={stops[1]} />
              <stop offset="100%" stopColor={stops[2]} />
            </linearGradient>
          </defs>
          <rect
            x="2"
            y="2"
            width={size - 4}
            height={size - 4}
            rx={RING_RADIUS}
            fill="none"
            stroke={`url(#${ringId})`}
            strokeWidth={RING_WIDTH}
            opacity={TRACK_OPACITY}
          />
          <rect
            x="2"
            y="2"
            width={size - 4}
            height={size - 4}
            rx={RING_RADIUS}
            pathLength="100"
            fill="none"
            stroke={`url(#${ringId})`}
            strokeWidth={RING_WIDTH}
            strokeLinecap="round"
            {...progressDashProps(pct, size)}
          />
        </svg>
      ) : null}
      {badge ? (
        <span
          className={`${styles.badge}${migrated ? ` ${styles.badgeGold}` : ''}`}
          style={{
            width: badgeBox.box,
            height: badgeBox.box,
            background: badgeDim
              ? `linear-gradient(var(--bg-chip), var(--bg-chip)) padding-box, linear-gradient(rgba(14,14,16,.7), rgba(14,14,16,.7)) border-box, ${ringBg} border-box`
              : `linear-gradient(var(--bg-chip), var(--bg-chip)) padding-box, ${ringBg} border-box`,
          }}
        >
          <SourceLogo source={source} size={badgeBox.mark} />
        </span>
      ) : null}
    </div>
  )
}

type ImagePhase = 'loading' | 'retry-wait' | 'loaded' | 'failed'

interface ImageLoadState {
  phase: ImagePhase
  attempt: 0 | 1
}

type ImageLoadAction = { type: 'loaded' | 'failed' | 'retry' }

function initialImageLoadState(src: string): ImageLoadState {
  const cached = tokenImageStatusCache.get(src)
  if (cached === 'loaded') return { phase: 'loaded', attempt: 0 }
  if (cached === 'failed') return { phase: 'failed', attempt: 0 }
  return { phase: 'loading', attempt: 0 }
}

function imageLoadReducer(state: ImageLoadState, action: ImageLoadAction): ImageLoadState {
  switch (action.type) {
    case 'loaded':
      return { ...state, phase: 'loaded' }
    case 'failed':
      if (state.phase !== 'loading' && state.phase !== 'loaded') return state
      return state.attempt === 0
        ? { phase: 'retry-wait', attempt: 1 }
        : { phase: 'failed', attempt: 1 }
    case 'retry':
      return state.phase === 'retry-wait' ? { ...state, phase: 'loading' } : state
  }
}

function TokenAvatarImage({
  src,
  alt,
  loading,
  fetchPriority,
  atomic,
}: {
  src: string
  alt: string
  loading: 'eager' | 'lazy'
  fetchPriority: 'high' | 'low' | 'auto'
  atomic: boolean
}) {
  const [state, dispatch] = useReducer(imageLoadReducer, src, initialImageLoadState)
  const imageRef = useRef<HTMLImageElement>(null)
  const [timeoutArmed, setTimeoutArmed] = useState(
    () => typeof IntersectionObserver === 'undefined'
  )

  const fail = useCallback(() => {
    if (state.phase !== 'loading' && state.phase !== 'loaded') return
    tokenImageStatusCache.delete(src)
    if (state.attempt === 1) tokenImageStatusCache.setFailed(src)
    dispatch({ type: 'failed' })
  }, [src, state.attempt, state.phase])

  useEffect(() => {
    if (state.phase !== 'loading' || timeoutArmed) return
    const image = imageRef.current
    if (!image) return
    if (typeof IntersectionObserver === 'undefined') {
      setTimeoutArmed(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        setTimeoutArmed(true)
        observer.disconnect()
      },
      { rootMargin: '256px' }
    )
    observer.observe(image)
    return () => observer.disconnect()
  }, [state.phase, timeoutArmed])

  useEffect(() => {
    if (state.phase === 'loading' && timeoutArmed) {
      const timeout = window.setTimeout(fail, TOKEN_IMAGE_LOAD_TIMEOUT_MS)
      return () => window.clearTimeout(timeout)
    }
    if (state.phase === 'retry-wait') {
      const retry = window.setTimeout(() => dispatch({ type: 'retry' }), TOKEN_IMAGE_RETRY_DELAY_MS)
      return () => window.clearTimeout(retry)
    }
    return undefined
  }, [fail, state.phase, timeoutArmed])

  if (state.phase === 'failed' || state.phase === 'retry-wait') {
    return atomic ? (
      <span className={styles.skeleton} data-token-avatar-skeleton="true" aria-hidden="true" />
    ) : null
  }

  return (
    <>
      {atomic && state.phase !== 'loaded' && (
        <span className={styles.skeleton} data-token-avatar-skeleton="true" aria-hidden="true" />
      )}
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        data-token-avatar-image="true"
        data-token-avatar-attempt={state.attempt}
        data-token-avatar-image-state={state.phase}
        style={{ opacity: atomic && state.phase !== 'loaded' ? 0 : 1 }}
        onLoad={() => {
          tokenImageStatusCache.setLoaded(src)
          dispatch({ type: 'loaded' })
        }}
        onError={fail}
      />
    </>
  )
}

const LAUNCHPAD_SOURCES = ['uranus', 'blum', 'tonfun', 'bigpump', 'gaspump', 'stonks']

function isLaunchpadSource(source?: string | null): boolean {
  return LAUNCHPAD_SOURCES.some((key) => source?.startsWith(key))
}
